import { useEffect, useState } from 'react';
import { apiUrl } from '../../../api/apiUrl';
import http from '../../../api/axios';
import DataTable from '../../../components/table/DataTable';
import { Link, useNavigate } from 'react-router-dom';
import Button from '../../../components/button/Button';
import { toast } from 'react-toastify';
import { useCurrentCompany } from '../../../hooks/useCompany';
import { paths } from '../../RouterContainer';
import { columns } from '../../../components/columns/Company/CurrencyRatesColumns';
import { CurrencyRate } from '../../../types/Currency';
import { PlusIcon } from '@heroicons/react/24/outline';
import { useTranslation } from '../../../context/translationContext';

interface Props {
  companyId: number;
}

/**
 * Page component represents currency rates for company
 */
export const CurrencyRatesPage = ({ companyId }: Props) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { company } = useCurrentCompany();
  const [currencyRates, setCurrencyRates] = useState<CurrencyRate[] | null>(null);
  const isCurrent = window.location.pathname.includes(`current`);

  /**
   * Loads currency rate for company
   */
  const fetchCurrencyRates = async () => {
    if (!company) return;
    const response = await http.apiGet({
      url: `${apiUrl.COMPANY_CURRENCY_RATES_LIST}?parentId=${company.id}`,
    });
    setCurrencyRates(response?.data);
  };

  useEffect(() => {
    fetchCurrencyRates();
  }, [company]);

  /**
   * Removes currency rate
   * @param id of entity
   */
  const onRemoveItem = async (id: string | number) => {
    if (!id) return;
    const response = await http.apiDelete({
      url: `${apiUrl.COMPANY_CURRENCY_RATES_DELETE}?id=${id}`,
    });
    if (response?.status === 204) {
      toast.success('Success!');
      fetchCurrencyRates();
    }
  };

  /**
   * Redirects to currency rate edit page
   * @param id
   */
  const onEditItem = (id: string | number) => {
    if (!id) return;
    navigate(
      `/${paths.MY_COMPANIES}/${isCurrent ? `current` : companyId}/${
        paths.COMPANY_CURRENCY_RATES
      }/edit/${id}`,
    );
  };

  return (
    <>
      <div className="flex justify-end">
        <Link to={`/${paths.MY_COMPANIES}/${isCurrent ? `current` : companyId}/${paths.COMPANY_CURRENCY_RATES}/create`}>
          <Button
            title={t('general.create-new')}
            color="green"
            icon={<PlusIcon className="w-4 h-4 mr-2" />}
          />
        </Link>
      </div>
      <DataTable
        data={currencyRates}
        columns={columns}
        handleRemove={onRemoveItem}
        handleEdit={onEditItem}
      />
    </>
  );
};
